import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useUser } from "util/UserContext";
import { sanitize } from "config/xssConfig";
import { PrivacyLevel } from "util/userUtils";
import api from "config/axiosConfig";
import { Comment } from "util/constants";
import { useResolvedIssue, type IssueEntityInput } from "hooks/useResolvedIssue";
import { CoauthorSuggestion } from "components/Coauthors/CoauthorSelect";
import CommentInput from "components/Comments/CommentInput";
import type { CommentInputContext, CommentInputSubmitHandle } from "components/Comments/CommentInput";
import type { MediaSlot } from "hooks/useMediaManager";
import { uploadEntityMediaDirect } from "util/entityMediaUpload";
import { apiRoutes } from "util/apiRoutes";
import { buildDraftStorageKey } from "util/draftStorage";
import { getDraftUiStrings, resolveDraftLocale } from "i18n/draftUi";
import { getFormsUiStrings, resolveFormsLocale } from "i18n/formsUi";
import { notify } from "lib/notify";

/**
 * Imperative handle exposed to the spill modal so its footer Submit
 * button can trigger the embedded `CommentInput`'s submit pipeline.
 *
 * Historical bug: `useImperativeHandle(ref, () => ({ submit: () => {} }))`
 * was a no-op while the spill modal expected this to actually submit.
 * The fix wires through `CommentInput.submitRef.current?.submit()` so
 * Submit in the spill footer triggers the same path as clicking Submit
 * inside the composer would. See `docs/architecture/forms-standard.md`.
 */
export interface ThreadFormHandle {
  submit: () => void;
}

interface Props {
  entity: IssueEntityInput;
  onSuccess: (comment: Comment) => void;
  onCancel: () => void;
  hideActions?: boolean;
  onCanSubmitChange?: (canSubmit: boolean) => void;
}

/**
 * ThreadFormBase
 *
 * Lightweight form used in the spill modal to create a root comment
 * (thread) under a selected Issue. Mirrors the UX of `CommentInput`
 * while exposing a real `submit()` handle so the modal footer controls
 * submission. Toasts route through `notify` for locale + duration
 * consistency (see `docs/architecture/forms-standard.md` section 4).
 */
const ThreadFormBase = forwardRef<ThreadFormHandle, Props>(({ entity, onSuccess, onCancel, onCanSubmitChange }, ref) => {
  const { currentUser, setShowAuthModal } = useUser();
  const { resolvedIssue } = useResolvedIssue(entity);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaSlot[]>([]);
  const draftUi = getDraftUiStrings(resolveDraftLocale());
  const formsUi = getFormsUiStrings(resolveFormsLocale());
  const commentDraftKey = currentUser && resolvedIssue
    ? buildDraftStorageKey("draft", "thread", "new", currentUser.id, resolvedIssue.id)
    : null;

  /**
   * Ref into the embedded `CommentInput` so the parent (spill modal)
   * can trigger its submit pipeline via the `useImperativeHandle`
   * below. Populated by passing `submitRef` to `CommentInput`.
   */
  const commentSubmitRef = useRef<CommentInputSubmitHandle | null>(null);

  const submit = async (
    content: string,
    privacy: PrivacyLevel,
    coauthors: CoauthorSuggestion[],
    context?: CommentInputContext | null
  ) => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    if (!resolvedIssue) return;
    setIsSubmitting(true);
    try {
      const safeHtml = sanitize(content);
      const response = await api.post<Comment>(apiRoutes.comments.issueThread(resolvedIssue.id), {
        content: safeHtml,
        properties: { privacy },
        place_id: context?.placeId ?? null,
        timeline_ts: context?.timelineTs ?? null,
      });
      const created = response.data;

      try {
        await api.post(apiRoutes.votes.comment(created.id), { value: 1 });
      } catch {
        /* best-effort */
      }

      if (coauthors && coauthors.length > 0 && privacy !== "anonymous") {
        try {
          await api.post(apiRoutes.coauthors.request("comment", created.id), {
            addedCoauthors: coauthors.map((c) => ({ id: c.id, type: c.type })),
          });
        } catch {
          /* best-effort */
        }
      }

      if (selectedMedia.length > 0) {
        try {
          await uploadEntityMediaDirect(
            "comment",
            created.id,
            selectedMedia.map((media) => ({
              file: media.file,
              order: media.order,
            }))
          );
        } catch {
          /* best-effort */
        }
      }

      notify.success({ key: "comment.added" }, { context: { surface: "thread-form-base" } });
      onSuccess(created);
    } catch (err) {
      notify.error(
        { key: "comment.addFailed" },
        { context: { surface: "thread-form-base", code: (err as { response?: { data?: { code?: string } } })?.response?.data?.code } }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveDraft = async (
    content: string,
    privacy: PrivacyLevel,
    coauthors: CoauthorSuggestion[],
    context?: CommentInputContext | null
  ) => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    if (!resolvedIssue) return;
    const safeHtml = sanitize(content);
    await api.post<Comment>(apiRoutes.comments.issueThread(resolvedIssue.id), {
      content: safeHtml,
      status: "draft",
      properties: {
        privacy,
        draft_coauthors: coauthors.map((coauthor) => ({ id: coauthor.id, type: coauthor.type })),
      },
      place_id: context?.placeId ?? null,
      timeline_ts: context?.timelineTs ?? null,
    });
    notify.success({ fallback: draftUi.draftSaved }, { context: { surface: "thread-form-base" } });
  };

  /**
   * Wire `submit()` to the embedded CommentInput's submit pipeline.
   * The spill modal footer button calls this; previously this returned
   * a no-op which silently broke the issue-thread spill kind.
   */
  useImperativeHandle(ref, () => ({
    submit: () => {
      if (isSubmitting) {
        return;
      }
      commentSubmitRef.current?.submit();
    },
  }), [isSubmitting]);

  return (
    <div className="flex flex-col gap-3 pb-4">
      <CommentInput
        action="add"
        buttonText={isSubmitting ? formsUi.submitting : formsUi.submit}
        onSubmit={(c, p, added, _newCoauthors, _removedCoauthors, _pollDraft, context) => {
          void submit(c, p, added, context);
        }}
        onCancel={onCancel}
        onSaveDraft={(c, p, added, _pollDraft, context) => saveDraft(c, p, added, context)}
        onMediaManagerChange={(media) => setSelectedMedia(media)}
        onCanSubmitChange={onCanSubmitChange}
        draftKey={commentDraftKey}
        draftEnabled={Boolean(commentDraftKey)}
        showTimelinePlace={true}
        submitRef={commentSubmitRef}
      />
    </div>
  );
});

ThreadFormBase.displayName = "ThreadFormBase";

export default ThreadFormBase;
